import type { SessionLogEntry } from '../types';

/** Total focus time in seconds across all completed pomodoro sessions (optionally date-filtered). */
export const totalFocusTime = (log: SessionLogEntry[], from?: string, to?: string): number =>
  log
    .filter(e => e.sessionType === 'pomodoro' && (!from || e.date >= from) && (!to || e.date <= to))
    .reduce((sum, e) => sum + e.actualDuration, 0);

/**
 * Returns focus minutes per day for the last `days` days, keyed by YYYY-MM-DD.
 * Counts all pomodoro sessions (completed or interrupted).
 */
export const focusByDay = (log: SessionLogEntry[], days = 182): Record<string, number> => {
  const result: Record<string, number> = {};
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days + 1);
  const cutoffStr = cutoff.toISOString().split('T')[0];

  log
    .filter(e => e.sessionType === 'pomodoro' && e.date >= cutoffStr)
    .forEach(e => {
      result[e.date] = (result[e.date] ?? 0) + Math.round(e.actualDuration / 60);
    });
  return result;
};

/**
 * Current streak: consecutive days with at least 1 completed pomodoro up to today.
 */
export const currentStreak = (log: SessionLogEntry[]): number => {
  const days = new Set(
    log.filter(e => e.sessionType === 'pomodoro' && e.completionStatus === 'completed').map(e => e.date)
  );
  if (days.size === 0) return 0;

  let streak = 0;
  const d = new Date();
  while (true) {
    const key = d.toISOString().split('T')[0];
    if (!days.has(key)) break;
    streak++;
    d.setDate(d.getDate() - 1);
  }
  return streak;
};

/**
 * Best (longest) historic streak — longest consecutive run of days with at least
 * 1 completed pomodoro, across the entire log.
 */
export const bestStreak = (log: SessionLogEntry[]): number => {
  const days = new Set(
    log.filter(e => e.sessionType === 'pomodoro' && e.completionStatus === 'completed').map(e => e.date)
  );
  if (days.size === 0) return 0;

  const sorted = Array.from(days).sort();
  let best = 1;
  let current = 1;

  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1]);
    const curr = new Date(sorted[i]);
    const diffMs = curr.getTime() - prev.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 1) {
      current++;
      if (current > best) best = current;
    } else {
      current = 1;
    }
  }
  return best;
};
